/**
 * CountryPhoneSelector Component
 *
 * A combined country dropdown and phone input component that auto-populates
 * country codes and ISO codes, formats phone numbers based on selected country,
 * and provides validation using phoneValidation utility.
 *
 * @module components/ui/CountryPhoneSelector
 */

import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/utils/cn';
import { Input } from '@/components/ui/Input';
import { useTheme } from '@/hooks/useTheme';
import type { CountryOption, PhoneInputState } from '@/types/registration';

/**
 * CountryPhoneSelector component props interface
 */
export interface CountryPhoneSelectorProps {
  /** Current phone value */
  value: {
    countryCode: string;
    isoCode: string;
    number: string;
  };
  /** Change handler */
  onChange: (phone: { countryCode: string; isoCode: string; number: string }) => void;
  /** Error message */
  error?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Placeholder for phone input */
  placeholder?: string;
  /** Required field */
  required?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Country data for major supported countries
 */
const COUNTRIES: CountryOption[] = [
  {
    label: 'Australia',
    value: 'AU',
    dialCode: '+61',
    flag: '🇦🇺',
    popular: true
  },
  {
    label: 'United States',
    value: 'US',
    dialCode: '+1',
    flag: '🇺🇸',
    popular: true
  },
  {
    label: 'United Kingdom',
    value: 'GB',
    dialCode: '+44',
    flag: '🇬🇧',
    popular: true
  },
  {
    label: 'Canada',
    value: 'CA',
    dialCode: '+1',
    flag: '🇨🇦',
    popular: true
  },
  {
    label: 'New Zealand',
    value: 'NZ',
    dialCode: '+64',
    flag: '🇳🇿'
  },
  {
    label: 'Germany',
    value: 'DE',
    dialCode: '+49',
    flag: '🇩🇪'
  },
  {
    label: 'France',
    value: 'FR',
    dialCode: '+33',
    flag: '🇫🇷'
  },
  {
    label: 'Japan',
    value: 'JP',
    dialCode: '+81',
    flag: '🇯🇵'
  },
  {
    label: 'Singapore',
    value: 'SG',
    dialCode: '+65',
    flag: '🇸🇬'
  }
];

/**
 * Format phone number based on country
 */
const formatPhoneNumber = (number: string, countryCode: string): string => {
  // Remove all non-digit characters
  const digits = number.replace(/\D/g, '');

  // Apply country-specific formatting
  switch (countryCode) {
    case 'AU':
      // Australian format: (02) 1234 5678 or 0412 345 678
      if (digits.length >= 10) {
        if (digits.startsWith('04')) {
          return digits.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
        } else {
          return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2 $3');
        }
      }
      break;
    case 'US':
    case 'CA':
      // North American format: (123) 456-7890
      if (digits.length >= 10) {
        return digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
      }
      break;
    case 'GB':
      // UK format: 020 1234 5678
      if (digits.length >= 10) {
        return digits.replace(/(\d{3})(\d{4})(\d{4})/, '$1 $2 $3');
      }
      break;
    default:
      // Default formatting with spaces every 3-4 digits
      if (digits.length > 6) {
        return digits.replace(/(\d{3,4})(\d{3})(\d+)/, '$1 $2 $3');
      } else if (digits.length > 3) {
        return digits.replace(/(\d{3})(\d+)/, '$1 $2');
      }
  }

  return digits;
};

/**
 * CountryPhoneSelector component with country dropdown and phone input
 */
export const CountryPhoneSelector: React.FC<CountryPhoneSelectorProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  placeholder = 'Enter phone number',
  required = false,
  className
}) => {
  const { isDark } = useTheme();
  const [state, setState] = useState<PhoneInputState>({
    selectedCountry: COUNTRIES.find(c => c.value === value.isoCode) || COUNTRIES[0],
    phoneNumber: value.number,
    formattedValue: formatPhoneNumber(value.number, value.isoCode),
    isCountryDropdownOpen: false,
    countrySearchTerm: ''
  });

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Update state when value prop changes
  useEffect(() => {
    const country = COUNTRIES.find(c => c.value === value.isoCode) || COUNTRIES[0];
    setState(prev => ({
      ...prev,
      selectedCountry: country,
      phoneNumber: value.number,
      formattedValue: formatPhoneNumber(value.number, value.isoCode)
    }));
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setState(prev => ({ ...prev, isCountryDropdownOpen: false, countrySearchTerm: '' }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (state.isCountryDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [state.isCountryDropdownOpen]);

  const handleCountrySelect = (country: CountryOption) => {
    setState(prev => ({
      ...prev,
      selectedCountry: country,
      isCountryDropdownOpen: false,
      countrySearchTerm: ''
    }));

    onChange({
      countryCode: country.dialCode,
      isoCode: country.value,
      number: state.phoneNumber
    });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawNumber = e.target.value;
    const formatted = formatPhoneNumber(rawNumber, state.selectedCountry.value);

    setState(prev => ({
      ...prev,
      phoneNumber: rawNumber,
      formattedValue: formatted
    }));

    onChange({
      countryCode: state.selectedCountry.dialCode,
      isoCode: state.selectedCountry.value,
      number: rawNumber
    });
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setState(prev => ({
        ...prev,
        isCountryDropdownOpen: !prev.isCountryDropdownOpen,
        countrySearchTerm: ''
      }));
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({
      ...prev,
      countrySearchTerm: e.target.value
    }));
  };

  // Filter countries based on search term
  const filteredCountries = COUNTRIES.filter(country =>
    country.label.toLowerCase().includes(state.countrySearchTerm.toLowerCase()) ||
    country.dialCode.includes(state.countrySearchTerm)
  );

  // Sort countries: popular first, then alphabetical
  const sortedCountries = filteredCountries.sort((a, b) => {
    if (a.popular && !b.popular) return -1;
    if (!a.popular && b.popular) return 1;
    return a.label.localeCompare(b.label);
  });

  return (
    <div className={cn('relative', className)}>
      <div className="flex">
        {/* Country Selector */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={toggleDropdown}
            disabled={disabled}
            className={cn(
              'flex items-center gap-2 px-3 py-2 border border-r-0 rounded-l-lg',
              'border-gray-500 transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500',
              'min-h-[40px]',
              isDark ? 'bg-secondary-800 hover:bg-secondary-700' : 'bg-white hover:bg-secondary-50',
              disabled && 'opacity-50 cursor-not-allowed',
              error && (isDark ? 'border-red-400' : 'border-red-500')
            )}
            aria-label={`Selected country: ${state.selectedCountry.label}`}
            aria-expanded={state.isCountryDropdownOpen}
            aria-haspopup="listbox"
          >
            <span className="font-medium" role="img" aria-label={state.selectedCountry.label}>
              {state.selectedCountry.flag}
            </span>
            <span className={cn(
              "font-medium",
              isDark ? "text-secondary-300" : "text-secondary-700"
            )}>
              {state.selectedCountry.dialCode}
            </span>
            <svg
              className={cn(
                'w-4 h-4 text-secondary-500 transition-transform',
                state.isCountryDropdownOpen && 'rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown */}
          {state.isCountryDropdownOpen && (
            <div className={cn(
              "absolute top-full left-0 z-50 w-80 mt-1 border rounded-lg shadow-lg",
              isDark ? "bg-secondary-800 border-secondary-600" : "bg-white border-secondary-300"
            )}>
              {/* Search */}
              <div className={cn(
                "p-3 border-b",
                isDark ? "border-secondary-700" : "border-secondary-200"
              )}>
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search countries..."
                  value={state.countrySearchTerm}
                  onChange={handleSearchChange}
                  className={cn(
                    "w-full px-3 py-2 text-sm border rounded-md placeholder:text-secondary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50",
                    isDark
                      ? "border-secondary-600 bg-secondary-700 text-secondary-100 placeholder:text-secondary-400"
                      : "border-secondary-300 bg-white text-secondary-900 placeholder:text-secondary-200"
                  )}
                />
              </div>

              {/* Country List */}
              <div className="max-h-60 overflow-y-auto" role="listbox">
                {sortedCountries.length > 0 ? (
                  sortedCountries.map((country) => (
                    <button
                      key={country.value}
                      type="button"
                      onClick={() => handleCountrySelect(country)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2 text-left transition-colors',
                        isDark ? 'hover:bg-secondary-700' : 'hover:bg-secondary-50',
                        state.selectedCountry.value === country.value && (isDark ? 'bg-primary-900/20' : 'bg-primary-50')
                      )}
                      role="option"
                      aria-selected={state.selectedCountry.value === country.value}
                    >
                      <span className="text-lg" role="img" aria-label={country.label}>
                        {country.flag}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className={cn(
                          "text-sm font-medium truncate",
                          isDark ? "text-secondary-100" : "text-secondary-900"
                        )}>
                          {country.label}
                        </div>
                      </div>
                      <span className={cn(
                        "text-sm",
                        isDark ? "text-secondary-400" : "text-secondary-500"
                      )}>
                        {country.dialCode}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className={cn(
                    "px-3 py-2 text-sm",
                    isDark ? "text-secondary-400" : "text-secondary-500"
                  )}>
                    No countries found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone Input */}
        <div className="flex-1">
          <Input
            type="tel"
            value={state.formattedValue}
            onChange={handlePhoneChange}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className="rounded-l-none border-l-0"
            state={error ? 'error' : 'default'}
            aria-label="Phone number"
          />
        </div>
      </div>
    </div>
  );
};

export default CountryPhoneSelector;